// ============================================================
// ELC Shift — Uploads / Match utilities module
//
// Levenshtein-based fuzzy name + store matching primitives used
// by every upload flow + staff bulk upload.
//
// Two distinct match domains, sharing the same Levenshtein helpers:
//   STORE matching   — "Hayat Plaza" → "Doha - Hyatt Plaza" etc.
//                      Used by Uploads.shared.findStoreColumnSuggestions
//                      and by mapAmanahStore.
//   PERSON matching  — "Mohamed Hassan" → "Mohammad Hasan" etc.
//                      Used by buildAmanahDiff to merge Amanah rows
//                      into the staff table.
//
// Public surface:
//   Uploads.match.normAmanahName(s)              — lowercase + collapse ws
//   Uploads.match.titleCaseName(s)               — title-case names, preserve hyphens
//   Uploads.match.designationToRole(designation) — Amanah → app role mapping
//   Uploads.match.levenshteinCapped(a, b, max)   — Levenshtein with early exit
//   Uploads.match.nameTokens(s, stopwords?)      — tokenise lowercase + drop short
//   Uploads.match.storeMatchScore(a, b)          — {score, explain} for stores
//   Uploads.match.bestStoreMatch(name, list, t?) — best match or null
//   Uploads.match.classifyNameMatch(a, b)        — 'strong'|'moderate'|'none'
//   Uploads.match.mapAmanahStore(amanahName)     — Amanah store → app store
//
// Free globals referenced as bare identifiers (lexical scope from
// admin.html — same convention as src/uploads/{budget,daily,addon,
// historical}.js):
//   - buildUploadNameMap   (admin.html helper)
//   - ALL_STORES           (admin.html canonical store list)
//   - staffData            (admin.html staff cache)
// ============================================================

// Valid app-side role values (after Designation mapping).
// Exported so admin.html UI can use this list for validation/display.
export const AMANAH_VALID_ROLES = [
  'Sales Assistant',
  'store_manager',
  'store in-charge',
  'retail_relief_manager',
  'Head Office',
  'area_manager',
];

// Store-name stopwords: words that add no discriminating value across
// stores. e.g. "Mall", "Plaza", "Centre" appear in many store names;
// matching on them alone gives false positives. We keep them in the
// full string but don't let token-overlap scoring reward them.
const STORE_STOPWORDS = new Set([
  'mall','plaza','centre','center','city','the','elc','doha',
  'dubai','abu','dhabi','al','ain','rak','uae','qatar','village',
  'mall2','2',
]);

// Normalise for comparison (lowercase, collapse whitespace).
export function normAmanahName(s) {
  return String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

// Title-case a name-ish string (handles ALL CAPS input gracefully,
// preserves hyphens/apostrophes).
export function titleCaseName(s) {
  return String(s || '').trim().replace(/\s+/g, ' ')
    .split(' ').map(w => {
      if (!w) return w;
      return w.split(/([-'])/).map(p => {
        if (p === '-' || p === "'") return p;
        return p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
      }).join('');
    }).join(' ');
}

// Map Amanah Designation → app role value.
export function designationToRole(designation) {
  const d = String(designation || '').toLowerCase().trim();
  if (d === 'store manager') return 'store_manager';
  if (d === 'sales assistant') return 'Sales Assistant';
  if (d === 'store in-charge' || d === 'store in charge') return 'store in-charge';
  // v58: Retail Relief Manager — Amanah file has typo "Reflief" we accept both
  if (d.includes('relief') || d.includes('reflief')) return 'retail_relief_manager';
  if (d.includes('head office')) return 'Head Office';
  if (d.includes('area')) return 'area_manager';
  return designation; // unknown — will flag as error
}

// v54: Fuzzy matching helpers (shared by store and name matching).
// Levenshtein with early exit when we exceed `max` — cheap enough for
// interactive use.
export function levenshteinCapped(a, b, max) {
  a = a || ''; b = b || '';
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > max) return max + 1;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    const curr = [i];
    let minRow = curr[0];
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i-1] === b[j-1] ? 0 : 1;
      curr[j] = Math.min(curr[j-1] + 1, prev[j] + 1, prev[j-1] + cost);
      if (curr[j] < minRow) minRow = curr[j];
    }
    if (minRow > max) return max + 1; // prune — row minimum exceeds budget
    prev = curr;
  }
  return prev[b.length];
}

// Tokenise a name/store string: lowercase, split on whitespace/hyphens/
// dashes, drop short noise tokens ('-', 'the', 'at', '&'). NOT used for
// store-specific noise words like 'mall' — callers strip those separately
// via the stopword list above.
export function nameTokens(s, stopwords) {
  stopwords = stopwords || new Set();
  return String(s || '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]+/gu, ' ')  // strip punctuation (keep letters/digits)
    .split(/\s+/)
    .filter(t => t.length >= 2 && !stopwords.has(t));
}

// Token-match Lev budget: scales with token length. Short tokens must
// match tightly (Lev 1) but longer tokens can absorb 2 edits (catches
// Hayat↔Hyatt, Mercato↔Marcato).
function _tokenLevBudget(len) { return len >= 5 ? 2 : 1; }

// Compare two store strings. Returns { score: 0..1, explain: string }.
// Score combines (a) token-overlap on content tokens and (b) Levenshtein
// distance on the normalised "core" string. 1.0 = exact or near-exact.
export function storeMatchScore(amanahName, appName) {
  if (!amanahName || !appName) return { score: 0, explain: '' };
  if (amanahName === appName) return { score: 1, explain: 'exact' };
  const a = String(amanahName).toLowerCase().trim();
  const b = String(appName).toLowerCase().trim();
  if (a === b) return { score: 1, explain: 'exact (case-insensitive)' };

  // Tokenise with stopwords stripped — we only reward overlap on meaningful words
  const ta = nameTokens(a, STORE_STOPWORDS);
  const tb = nameTokens(b, STORE_STOPWORDS);
  if (!ta.length || !tb.length) return { score: 0, explain: 'empty after stopwords' };

  // For each token in the shorter set, find a match (exact or Lev within
  // length budget) in the longer set.
  const [shorter, longer] = ta.length <= tb.length ? [ta, tb] : [tb, ta];
  const used = new Set();
  let matched = 0;
  for (const t of shorter) {
    const budget = _tokenLevBudget(t.length);
    for (let i = 0; i < longer.length; i++) {
      if (used.has(i)) continue;
      const other = longer[i];
      if (t === other) { matched++; used.add(i); break; }
      // Only allow fuzzy if token lengths are comparable — avoids "al" matching "all"
      if (Math.abs(t.length - other.length) <= budget &&
          t.length >= 3 && other.length >= 3 &&
          levenshteinCapped(t, other, budget) <= budget) {
        matched++; used.add(i); break;
      }
    }
  }
  const tokenScore = matched / shorter.length;

  // Plus a full-string Lev on the de-stopworded concatenation — catches
  // dropped spaces etc.
  const aCore = ta.join(' '); const bCore = tb.join(' ');
  const lev = levenshteinCapped(aCore, bCore, Math.max(aCore.length, bCore.length));
  const levScore = lev >= 0 ? Math.max(0, 1 - lev / Math.max(aCore.length, bCore.length)) : 0;

  // Weighted combination: token overlap dominates
  const score = tokenScore * 0.75 + levScore * 0.25;
  return { score, explain: 'tokens ' + matched + '/' + shorter.length + ' (lev=' + lev + ')' };
}

// Find the best-matching app store for an Amanah store name.
// Returns { appName, score } or null. threshold default is 0.6 (tuned on real data).
export function bestStoreMatch(amanahName, candidates, threshold) {
  threshold = threshold != null ? threshold : 0.6;
  let best = null, second = 0;
  for (const c of candidates) {
    const { score } = storeMatchScore(amanahName, c);
    if (!best || score > best.score) { second = best ? best.score : 0; best = { appName: c, score }; }
    else if (score > second) second = score;
  }
  if (!best) return null;
  if (best.score < threshold) return null;
  // Require a reasonable gap between best and second — avoids ambiguity
  // (e.g. "Marina Mall" matches Abu Dhabi Marina Mall AND Dubai Marina
  // Mall). If gap is too small, return null.
  if (best.score - second < 0.1 && second >= threshold) return null;
  return best;
}

// Name match classification: 'strong' (auto-merge) | 'moderate' (surface
// as suggestion) | 'none'.
//   Strong: first+last tokens both match (with ≤1 char typo tolerance on first name)
//   Moderate: ≥2 tokens overlap across the two names
export function classifyNameMatch(a, b) {
  const ta = nameTokens(a); // no stopwords — every name token counts
  const tb = nameTokens(b);
  if (ta.length === 0 || tb.length === 0) return 'none';

  const firstA = ta[0], lastA = ta[ta.length - 1];
  const firstB = tb[0], lastB = tb[tb.length - 1];

  const firstMatch = firstA === firstB || (firstA.length >= 4 && levenshteinCapped(firstA, firstB, 1) <= 1);
  const lastMatch  = lastA === lastB   || (lastA.length  >= 4 && levenshteinCapped(lastA,  lastB,  1) <= 1);

  if (firstMatch && lastMatch) return 'strong';

  // Count any-position token overlap for moderate
  const used = new Set();
  let overlap = 0;
  for (const t of ta) {
    for (let i = 0; i < tb.length; i++) {
      if (used.has(i)) continue;
      if (t === tb[i] || (t.length >= 4 && levenshteinCapped(t, tb[i], 1) <= 1)) {
        overlap++; used.add(i); break;
      }
    }
  }
  if (overlap >= 2) return 'moderate';
  return 'none';
}

// Map an Amanah store name to an app store name.
// Tries exact match → uppercase match → "ELC - " prefix match → fuzzy
// fallback via bestStoreMatch.
export function mapAmanahStore(amanahName) {
  if (!amanahName) return null;
  const cleaned = String(amanahName).trim();
  const map = buildUploadNameMap();
  if (map[cleaned]) return map[cleaned];
  if (map[cleaned.toUpperCase()]) return map[cleaned.toUpperCase()];
  // Try with "ELC - " prefix (legacy format where uppercase ELC-prefixed
  // names are in STORE_NAME_MAP).
  if (map['ELC - ' + cleaned.toUpperCase()]) return map['ELC - ' + cleaned.toUpperCase()];

  // v54: fuzzy match fallback. When the exact/ELC-prefix lookups miss,
  // try token-overlap matching against all known app stores. Catches
  // "Dar Al Salam Mall" → "Doha - Dar Al Salam", "Hayat Plaza" → "Doha -
  // Hyatt Plaza" (typo), etc.
  const storeSet = new Set(ALL_STORES);
  staffData.forEach(s => { if (s.store) storeSet.add(s.store); });
  const candidates = [...storeSet];
  const best = bestStoreMatch(cleaned, candidates);
  return best ? best.appName : null;
}
